/**
 * Canonical Google Business Profile URL. Ties the site to the verified
 * profile via schema `sameAs`, and backs the "Find us on Google" link on
 * the contact page.
 *
 * This is the permanent share link from the GBP dashboard (Share, Copy link).
 * It redirects to the Maps place page, which is what Google follows.
 *
 * Do NOT replace it with a `google.com/search?…&stick=…` URL: the `stick`,
 * `mat`, and `authuser` params on those are session-scoped and are not a
 * stable entity identifier. Upgrading this to the full
 * `https://www.google.com/maps/place/…` permalink is fine and needs no other
 * change, since every consumer reads this constant.
 *
 * If this is ever set back to an empty string it is filtered out of `sameAs`
 * rather than emitted as `""`, and the contact page hides its Google link.
 */
export const GOOGLE_BUSINESS_PROFILE_URL = 'https://share.google/KcmfCLiTwKUBlKYm0'

export const BUSINESS = {
  name: 'Clear Guard',
  legalName: 'Clear Guard',
  /**
   * Exact name on the Google Business Profile. Longer than `name` because
   * GBP carries the category words that disambiguate the brand.
   *
   * "Clear Guard" collides with at least two established entities that
   * outrank us on the bare string: a dental-products company
   * (theclearguard.com, with BBB, Trustpilot, and Sitejabber profiles) and a
   * San Francisco auto-glass shop (Yelp). Google resolves an ambiguous brand
   * query toward whichever entity has the most third-party corroboration, so
   * every surface that can carry the full GBP name should carry it verbatim:
   * schema `alternateName`, citations, directory listings. A mismatch between
   * the site and the profile is a reconciliation failure, not a cosmetic
   * difference.
   */
  gbpName: 'Clear Guard Security Window Film & Home Fortification',
  googleBusinessProfile: GOOGLE_BUSINESS_PROFILE_URL,
  description:
    'Clear Guard installs security window film and door fortification for homes and businesses across Toronto and the GTA, reinforcing windows, glass doors, and entry points to delay forced entry.',
  phone: '(416) 907-6900',
  email: 'fortify@clearguard.ca',
  domain: 'https://clearguard.ca',
  address: {
    streetAddress: '8854 Yonge St Suite 326',
    addressLocality: 'Richmond Hill',
    addressRegion: 'ON',
    postalCode: 'L4C 0T4',
    addressCountry: 'CA',
  },
  geo: { latitude: 43.84301, longitude: -79.43113 },
  priceRange: '$$',
  // Mon-Sun, confirmed by the owner 2026-09-02. This is the single source of
  // truth for schema.org `openingHours`; the footer and /contact spell the
  // same range out in prose. All three, plus the Google Business Profile,
  // have to agree: inconsistent hours across a site and its profile is a
  // local-entity trust signal, not a copy nit.
  hours: 'Mo-Su 09:00-19:00',
  /**
   * Must match the live Google Business Profile. This ships as
   * `aggregateRating` in LocalBusiness JSON-LD on the homepage and /contact,
   * so a number that does not match a publicly verifiable source is
   * fabricated structured data and a manual-action risk. Confirmed 5.0 / 19
   * on 2026-09-02. Re-check it whenever the review count moves.
   */
  rating: { value: 5.0, count: 19 },
  /**
   * Third-party profiles that corroborate this entity. Order matters only
   * for readability, but the Google Business Profile should stay first: it
   * is the strongest site-to-entity link Google can follow.
   *
   * Empty entries are filtered so an unset GBP URL never ships as `""`,
   * which would be an invalid `sameAs` value.
   */
  sameAs: [
    GOOGLE_BUSINESS_PROFILE_URL,
    'https://instagram.com/ClearGuardGTA',
    'https://www.youtube.com/@ClearGuardGTA',
  ].filter(Boolean) as string[],
} as const

// YouTube IDs for the in-page video lightboxes. Set these once and every
// surface (door page hero, DemoTestVideoSection, V2TestingVideos, etc.) updates.
export const VIDEO_IDS = {
  // Testing-facility reinforced-door footage — homepage card + DemoTestVideoSection lightbox
  doorReinforcementTest: 'Fu22FQU7VSQ',
  // Door-kick demo on the door fortification page hero
  doorKickHero: 'DMveUfWPeMg',
  patioComparison: 'feBWb5-qO7s',
  sidelightHammer: 'n2lwUjL3tTo',
  filmImpactTest: 'bswEunSLYWc',
} as const
